import { useQuery } from '@powersync/react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';

import { queries, type SidebarViewRow } from '@flow/sync';

import { t } from '../i18n/es.js';
import { InboxView } from '../inbox/InboxView.jsx';
import { ListView } from '../list/ListView.jsx';
import { useSession } from '../session.jsx';
import { useSetViewHeader } from '../shell/AppLayout.jsx';

/** Flow list views with a screen: ASAP (f1-asap-view), Backlog, Algún día, Seguimiento. */
const LIST_VIEWS = new Set(['asap', 'backlog', 'someday', 'waiting']);

/**
 * A view of the user by its key (`/v/$viewKey`, VIS-01). The header shows the view's current
 * name, so renaming it shows up here too. Each view gets its screen in its own change (Inbox,
 * ASAP, Backlog…); until then the content says so instead of pretending.
 */
export function ViewPage() {
  const { viewKey } = useParams({ from: '/app/v/$viewKey' });
  const { tarea } = useSearch({ from: '/app/v/$viewKey' });
  const navigate = useNavigate();
  const { user, local } = useSession();
  const query = queries.viewByKey(user?.id ?? '', viewKey);
  const { data, isLoading } = useQuery<SidebarViewRow>(query.sql, query.params);
  const view = data[0];
  const setHeader = useSetViewHeader();

  useEffect(() => {
    if (isLoading) return;
    setHeader({ title: view ? view.name : t.views.notFoundTitle });
  }, [isLoading, view, setHeader]);

  if (isLoading) return null;
  if (!view) {
    return (
      <section className="empty-state" role="status" data-testid="view-not-found">
        <p>{t.views.notFound(viewKey)}</p>
      </section>
    );
  }
  // The Inbox has its own screen (f1-triage); the other views get theirs in their changes.
  if (view.key === 'inbox' && user) return <InboxView userId={user.id} />;
  if (view.key && LIST_VIEWS.has(view.key) && user && local) {
    return (
      <ListView
        key={view.key}
        userId={user.id}
        local={local}
        viewKey={view.key}
        openTaskId={tarea ?? null}
        onOpenTask={(id) =>
          void navigate({
            to: '/v/$viewKey',
            params: { viewKey },
            search: id ? { tarea: id } : {},
            replace: !id,
          })
        }
      />
    );
  }
  return (
    <section className="empty-state" data-testid="view-placeholder" data-view={view.key}>
      <p>{t.views.comingSoon(view.name)}</p>
    </section>
  );
}

/** Rituals of the sidebar (docs/05 §3). The weekly review screen arrives in F2. */
export function RitualPage() {
  const { ritualKey } = useParams({ from: '/app/r/$ritualKey' });
  const { user } = useSession();
  const query = queries.sidebarRituals(user?.id ?? '');
  const { data } = useQuery<{ key: string; name: string }>(query.sql, query.params);
  const ritual = data.find((row) => row.key === ritualKey);
  const setHeader = useSetViewHeader();

  useEffect(() => {
    setHeader({ title: ritual?.name ?? t.views.notFoundTitle });
  }, [ritual, setHeader]);

  return (
    <section className="empty-state" data-testid="ritual-placeholder">
      <p>{ritual ? t.views.ritualComingSoon(ritual.name) : t.views.notFound(ritualKey)}</p>
    </section>
  );
}
